import * as mui from '@mui/material'
import {Field, Form, Formik, FormikValues} from 'formik'
import {TextField} from 'formik-mui'
import React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { langSwitchContext, translationsContext } from 'context/translations'
import { Link } from 'components/Link'
import { userContext } from 'context/user'
import { routes } from 'routes'
import { graphqlClient, requireErrorExtensions } from 'utils/graphql'

import logoIcon from '../assets/logo.png'
import { LanguageSelector } from '../LanguageSelector'


const validationSchema = Yup.object().shape({
  email: Yup.string().email().required('Required'),
})

export function ForgotPassword() {
  const [language, _] = React.useContext(langSwitchContext)
  const t = React.useContext(translationsContext)
  const user = React.useContext(userContext)

  const [commonErrors, setCommonErrors] = React.useState<string[]>([])

  const navigate = useNavigate()

  function handleSubmit(
    values: FormikValues,
    {setSubmitting}: {setSubmitting: (isSubmitting: boolean) => void}
  ) {
    setCommonErrors([])
    graphqlClient.chain.mutation
      .forgotPassword({email: values.email, language: language })
      .execute({__scalar: 1})
      .then(() => navigate(routes.LOGIN+'?reset_sent=true'))
      .catch(e => {
        const {common_errors} = requireErrorExtensions(e)
        // This form renders no field errors, so a GraphQL error without common_errors
        // is nothing we can show the user — let it reach Sentry.
        if (!common_errors) throw e
        setCommonErrors(common_errors)
      })
      .finally(() => setSubmitting(false))
  }

  if (user.data) return <Navigate to={routes.INDEX} />
  
  return (
    <mui.Container component="main" maxWidth="xs">
      <mui.Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src={logoIcon} alt="logo" height="100"/>
        <mui.Typography component="h1" variant="h5" mt={4}>
          {t('forgot_password__title')}
        </mui.Typography>
        <Formik
          initialValues={{email: ''}}
          onSubmit={handleSubmit}
          sx={{mt: 1}}
          validateOnBlur={true}
          validateOnChange={false}
          validationSchema={validationSchema}
        >
          {({submitForm, isSubmitting, touched, errors, isValid}) => (
            <Form style={{width: '100%'}}>
              <mui.Stack spacing={3} mt={3}>
                {commonErrors.map((error, i) => (
                  <mui.Alert key={i} severity="error">
                    {t(`api__${error}`)}
                  </mui.Alert>
                ))}
                <Field
                  component={TextField}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    autoComplete: 'email',
                    fullWidth: true,
                  }}
                  InputLabelProps={{shrink: true}}
                  label={t('forgot_password__field__email')}
                  name="email"
                />
                <mui.Button
                  disabled={isSubmitting || !isValid}
                  fullWidth
                  onClick={submitForm}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    backgroundColor: '#A8C02A',
                    '&:hover': {
                      backgroundColor: '#C1CC00',
                    },
                }}
                  variant="contained"
                >
                  {t('forgot_password__button__submit')}
                </mui.Button>
                <mui.Stack direction="row" justifyContent="space-between">
                  <mui.Stack>
                    <Link to={routes.LOGIN} variant="body2">
                      {t('forgot_password__link__back_to_login')}
                    </Link>
                  </mui.Stack>
                  <LanguageSelector />
                </mui.Stack>
              </mui.Stack>
            </Form>
          )}
        </Formik>
      </mui.Box>
    </mui.Container>
  )
}